import { useState } from "react";
import { ClipLoader } from "react-spinners";
import { Icon, IconName } from "../icons/Icon";

interface BaseButtonProps {
    children: React.ReactNode;
    onClick?: () => void;
    variant: "primary" | "secondary" | "tertiary" | "dark";
    pulse?: boolean;
}

interface AlwaysEnabledButtonProps extends BaseButtonProps {
    icon?: IconName;
    disabled?: false;
}

interface PossibleDisabledButtonProps extends BaseButtonProps {
    icon?: "envelope" | "left" | "sync";
    disabled?: boolean;
}

type ButtonProps = AlwaysEnabledButtonProps | PossibleDisabledButtonProps;

export const Button = ({
    children,
    onClick,
    variant,
    icon,
    disabled,
    pulse,
}: ButtonProps) => {
    const [isHandlingClick, setIsHandlingClick] = useState(false);

    const handleClick = async (e: React.MouseEvent) => {
        if (!onClick) return;

        e.preventDefault();
        e.stopPropagation();
        try {
            setIsHandlingClick(true);
            await onClick?.();
            setIsHandlingClick(false);
        } catch (error) {
            setIsHandlingClick(false);
            throw error;
        }
    };

    return (
        <div
            className={`relative button-${variant} ${
                disabled ? "disabled" : "enabled"
            } ${pulse ? "button-pulse-animate" : ""}`}
        >
            {!disabled && <div className={`button-shadow`} />}
            <button
                onClick={handleClick}
                className={`w-full flex items-center justify-center gap-2 ${
                    disabled ? "disabled" : ""
                }`}
                disabled={disabled || isHandlingClick}
            >
                {icon && (
                    <div className="absolute left-4">
                        <Icon
                            name={icon as IconName}
                            width={15}
                            height={15}
                            disabled={disabled || isHandlingClick}
                        />
                    </div>
                )}
                {isHandlingClick && <ClipLoader size={24} color="unset" />}
                {children}
            </button>
        </div>
    );
};
